import {ChakraProvider, Container} from "@chakra-ui/react";
import React, {useEffect} from "react";
import {QueryClient, QueryClientProvider} from "react-query";
import {BrowserRouter, Navigate, Route, Routes} from "react-router-dom";

import Admin from "./admin/components/Admin";
import CandidatesList from "./admin/components/CandidatesList";
import CreateCertificates from "./admin/components/CreateCertificates";
import EDICandidatesList from "./admin/components/EDICandidates";
import GlobalProperties from "./admin/components/GlobalProperties";
import TestDetail from "./admin/components/TestDetail";
import UsersList from "./admin/components/UsersList";
import IntroAvatai from "./avatai/IntroAvatai";
import RegisterAvatai from "./avatai/RegisterAvatai";
import ResendEmailAvatai from "./avatai/ResendEmailAvatai";
import Home from "./Home";
import AutoLogin from "./shared/components/AutoLogin";
import CandidateDetail from "./shared/components/CandidateDetail";
import CandidateResult from "./shared/components/CandidateResult";
import EDICandidateResult from "./shared/components/EDICandidateResult";
import Error from "./shared/components/Error";
import Footer from "./shared/components/Footer";
import Header from "./shared/components/Header";
import IntroForm from "./shared/components/IntroForm";
import IntroFormDoE from "./shared/components/IntroFormDoE";
import IntroFormEDI from "./shared/components/IntroFormEDI";
import LicenseResult from "./shared/components/LicenseResult";
import LicensesList from "./shared/components/LicensesList";
import Login from "./shared/components/Login";
import NotFound from "./shared/components/NotFound";
import RequireAuth from "./shared/components/RequireAuth";
import TestLogin from "./shared/components/TestLogin";
import { AuthenticationProvider } from "./shared/hooks/AuthenticationProvider";
import { FontProvider } from "./shared/hooks/FontProvider";
import { ThemeProvider } from "./shared/hooks/ThemeProvider";
import {Authority, Paths} from "./shared/model/Constants";
import {isInAvataiSite} from "./shared/Utils";
import AutomatedTest from "./test/components/AutomatedTest";
import RenderTest from "./test/components/Test";
import Theme from "./theme/Theme";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false
    },
  },
});
const App = () => {
  function authenticated(children: JSX.Element, authority?: Authority, redirectTo?: string) {
    return <RequireAuth authority={authority} redirectTo={redirectTo}>{children}</RequireAuth>;
  }
  const isAvatai = isInAvataiSite();
  const containerProps = {
    padding:isAvatai?"":"4",
    paddingTop:isAvatai?"":"1",
    height:isAvatai?"":"100%",
  };
  useEffect(() => {
    document.title = isAvatai?"AvatAI":'Pruebas de Dominio Lingüístico';
  }, [isAvatai]);
  return (
    <ChakraProvider theme={Theme}>
      <QueryClientProvider client={queryClient}>
        <AuthenticationProvider>
        <ThemeProvider>
        <FontProvider>
          <BrowserRouter>
            <Header />
            <Container boxSizing="content-box" id="container" maxW="100%" width="auto"
                       paddingInline={0}
                       {...containerProps}
                       position={{ base: 'relative', md:'static' }}  >
              <Routes>
                <Route path={Paths.ERROR} element={<Error />} />
                <Route path="*" element={<NotFound />} />

                <Route path={Paths.BASE} element={<Navigate to={Paths.HOME} />} />
                <Route path={Paths.API} element={<Navigate to={Paths.HOME} />} />
                <Route path={Paths.HOME} element={authenticated(<Home />)} />
                <Route path={Paths.HOME_START_TEST} element={authenticated(<Home />)} />

                <Route path={Paths.AUTO_LOGIN} element={<AutoLogin />} />
                <Route path={Paths.LOGIN} element={<Login />} />
                <Route path={Paths.LOGIN_FORM} element={<IntroForm />} />
                <Route path={Paths.LOGIN_FORMD} element={<IntroFormDoE />} />
                <Route path={Paths.LOGIN_FORM_EDI} element={<IntroFormEDI />} />

                <Route path={Paths.LOGIN_DEMO} element={<Login isDemo={true}/>} />

                <Route path={Paths.EDI_CANDIDATES} element={<EDICandidatesList />} />
                <Route path={Paths.EDI_CANDIDATE_RESULT} element={<EDICandidateResult />} />
                <Route path={Paths.PUBLIC_CANDIDATE_RESULT} element={<EDICandidateResult />} />
                <Route path={Paths.PUBLIC_LICENSE_RESULT} element={<LicenseResult />} />
                <Route path={Paths.PUBLIC_LICENSES_INST} element={<LicensesList isPublic={true} />} />
                <Route path={Paths.PUBLIC_CANDIDATES} element={<CandidatesList />} />
                <Route path={Paths.PUBLIC_CANDIDATES_INST} element={<CandidatesList />} />

                <Route path={Paths.TEACHER_CANDIDATE_RESULT} element={authenticated(<CandidateResult />, Authority.ROLE_TEACHER)} />
                <Route path={Paths.TEACHER_LICENSES_INST} element={authenticated(<LicensesList />, Authority.ROLE_TEACHER)} />
                <Route path={Paths.TEACHER_CANDIDATES} element={authenticated(<CandidatesList />, Authority.ROLE_TEACHER)} />

                <Route path={Paths.JUDGE_CANDIDATES} element={authenticated(<CandidatesList />, Authority.ROLE_JUDGE)} />

                <Route path={Paths.CANDIDATE_DETAIL_PUBLIC_ID} element={<CandidateDetail />} />

                <Route path={Paths.TESTS_PUBLIC_ID_LOGIN} element={<TestLogin />} />
                <Route path={Paths.TESTS_PUBLIC_ID} element={authenticated(<RenderTest />, undefined, Paths.TESTS_PUBLIC_ID_LOGIN)} />
                <Route path={Paths.AUTOMATED_TEST} element={authenticated(<AutomatedTest />, undefined, Paths.LOGIN)} />
                <Route path={Paths.SPEECH_TEST} element={authenticated(<AutomatedTest />, undefined, Paths.LOGIN)} />

                <Route
                  path={Paths.ADMIN_TESTS_ID}
                  element={authenticated(<TestDetail />, Authority.ROLE_ADMIN)}
                />
                <Route path={Paths.ADMIN} element={authenticated(<Admin />, Authority.ROLE_ADMIN)} />
                <Route path={Paths.ADMIN_CANDIDATES} element={authenticated(<CandidatesList />, Authority.ROLE_ADMIN)} />
                <Route path={Paths.ADMIN_USERS} element={authenticated(<UsersList />, Authority.ROLE_ADMIN)} />
                <Route path={Paths.ADMIN_GLOBAL_PROPERTIES} element={authenticated(<GlobalProperties />, Authority.ROLE_ADMIN)} />

                <Route path={Paths.CREATE_PDFS} element={<CreateCertificates />} />

                <Route path={Paths.AVATAI_INTRO} element={<IntroAvatai/>} />
                <Route path={Paths.AVATAI_REGISTER} element={<RegisterAvatai/>} />
                <Route path={Paths.AVATAI_RESEND_EMAIL} element={<ResendEmailAvatai/>} />

              </Routes>
            </Container>
            <Footer />
          </BrowserRouter>
        </FontProvider>
        </ThemeProvider>
        </AuthenticationProvider>
      </QueryClientProvider>
    </ChakraProvider>
  );
};

export default App;